import { Pill } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { CatalogPage } from "@/components/common/CatalogPage";
import { medicinesApi } from "@/lib/api";
import type { Medicine } from "@/lib/api/types";

/** The medicine reference list doctors prescribe from. */
export default function MedicinesPage() {
  return (
    <CatalogPage<Medicine>
      queryKey="medicines"
      title="Medicines"
      subtitle={(n) => `${n} medicines in the reference list doctors prescribe from.`}
      icon={Pill}
      noun="medicine"
      searchPlaceholder="Search by name or generic name…"
      source={{ kind: "paged", fetch: medicinesApi.list }}
      itemLabel={(m) => m.name}
      columns={[
        { header: "Name", render: (m) => <span className="font-semibold">{m.name}</span> },
        { header: "Generic", render: (m) => m.genericName },
        { header: "Form / strength", render: (m) => [m.dosageForm, m.strength].filter(Boolean).join(" · ") || "—" },
        { header: "Prescription", render: (m) => (m.requiresPrescription ? <Badge variant="warning">Rx only</Badge> : <Badge variant="muted">OTC</Badge>) },
      ]}
      fields={[
        { key: "name", label: "Name", required: true, half: true, placeholder: "Panadol" },
        { key: "genericName", label: "Generic name", required: true, half: true, placeholder: "Paracetamol" },
        { key: "brandNames", label: "Brand names", placeholder: "Panadol, Calpol" },
        { key: "category", label: "Category", half: true },
        { key: "drugClass", label: "Drug class", half: true },
        { key: "dosageForm", label: "Dosage form", half: true, placeholder: "Tablet" },
        { key: "strength", label: "Strength", half: true, placeholder: "500 mg" },
        { key: "atcCode", label: "ATC code", half: true },
        { key: "pregnancyCategory", label: "Pregnancy category", half: true },
        { key: "description", label: "Description", type: "textarea" },
        { key: "uses", label: "Uses", type: "textarea" },
        { key: "sideEffects", label: "Side effects", type: "textarea" },
        { key: "contraindications", label: "Contraindications", type: "textarea" },
        { key: "interactions", label: "Interactions", type: "textarea" },
        { key: "requiresPrescription", label: "Requires a prescription", type: "checkbox" },
      ]}
      create={medicinesApi.create}
      update={medicinesApi.update}
      remove={medicinesApi.remove}
    />
  );
}
